import React, { useEffect, useMemo, useState } from "react";
import {
	Box,
	Button,
	Chip,
	InputAdornment,
	Paper,
	Stack,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TablePagination,
	TableRow,
	TableSortLabel,
	TextField,
	Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import TravelExploreOutlinedIcon from "@mui/icons-material/TravelExploreOutlined";
import StorefrontOutlinedIcon from "@mui/icons-material/StorefrontOutlined";
import BoltOutlinedIcon from "@mui/icons-material/BoltOutlined";
import HomeWorkOutlinedIcon from "@mui/icons-material/HomeWorkOutlined";
import AccountBalanceWalletOutlinedIcon from "@mui/icons-material/AccountBalanceWalletOutlined";
import MonetizationOnOutlinedIcon from "@mui/icons-material/MonetizationOnOutlined";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import dayjs from "dayjs";
import { GlobalState } from "context/GlobalContext";
import { AuthState } from "context/AuthContext";
import { pythonVmInstance } from "utils/AxiosInstances/RareboxPythonInstance";
import { canDownloadEboStoreExpenses, eboStoreExpensesRoleAccess } from "./data";

const formatCurrency = (value) => `₹ ${Number(value || 0).toLocaleString("en-IN")}`;

const formatMonth = (value) => {
	const parsed = dayjs(value);
	return parsed.isValid() ? parsed.format("MMM YYYY") : value || "-";
};

const toNumber = (value) => {
	const parsed = Number(String(value ?? "").replace(/[^0-9.-]/g, ""));
	return Number.isFinite(parsed) ? parsed : 0;
};

const toCsvValue = (value) => {
	if (value === null || value === undefined) return '""';
	const stringValue = typeof value === "object" ? JSON.stringify(value) : String(value);
	return `"${stringValue.replace(/"/g, '""')}"`;
};

const formatDateForCsv = (value) => {
	if (value === null || value === undefined || value === "") return "";
	const parsed = dayjs(value);
	return parsed.isValid() ? parsed.format("YYYY-MM-DD") : String(value);
};

const isDateColumnKey = (key) => /date/i.test(String(key || "")) || ["month", "month_year", "monthYear"].includes(key);

const getCsvHeaders = (rows = []) => {
	const allHeaders = Array.from(
		rows.reduce((set, row) => {
			Object.keys(row || {}).forEach((key) => set.add(key));
			return set;
		}, new Set()),
	);

	const ledgerKey = allHeaders.find((header) => header.toLowerCase() === "ledger");
	const dateKey =
		allHeaders.find((header) => header.toLowerCase() === "entry_date") ||
		allHeaders.find((header) => isDateColumnKey(header));
	const tagKey = allHeaders.find((header) => header.toLowerCase() === "tag");

	const priorityHeaders = [ledgerKey, dateKey, tagKey].filter(Boolean);
	const remainingHeaders = allHeaders.filter((header) => !priorityHeaders.includes(header));

	return [...priorityHeaders, ...remainingHeaders];
};

const formatCsvCellValue = (header, value) => {
	if (isDateColumnKey(header)) {
		return toCsvValue(formatDateForCsv(value));
	}
	return toCsvValue(value);
};

const downloadRawCsv = (rows, fileName) => {
	if (!rows?.length) return;

	const headers = getCsvHeaders(rows);
	const csvRows = [
		headers.join(","),
		...rows.map((row) => headers.map((header) => formatCsvCellValue(header, row?.[header])).join(",")),
	];

	const blob = new Blob([csvRows.join("\n")], { type: "text/csv;charset=utf-8;" });
	const url = URL.createObjectURL(blob);
	const link = document.createElement("a");
	link.href = url;
	link.setAttribute("download", fileName);
	document.body.appendChild(link);
	link.click();
	document.body.removeChild(link);
	URL.revokeObjectURL(url);
};

const toMonthKey = (value) => {
	// dayjs(undefined) defaults to "now", which incorrectly pulls no-ledger stores into the current month
	if (value === null || value === undefined || value === "") return "";
	const parsed = dayjs(value);
	return parsed.isValid() ? parsed.format("YYYY-MM") : "";
};

const filterByDateRange = (data = [], from, to) => {
	if (!from?.isValid() || !to?.isValid()) return [];
	const start = from.startOf("day");
	const end = to.endOf("day");
	return data.filter((row) => {
		const monthKey = row?.monthKey || toMonthKey(row?.month ?? row?.month_year);
		// Keep stores that have no ledger/Entry_Date so the full store list is shown
		if (!monthKey) return true;
		const monthStart = dayjs(`${monthKey}-01`).startOf("month");
		const monthEnd = monthStart.endOf("month");
		return !monthEnd.isBefore(start, "day") && !monthStart.isAfter(end, "day");
	});
};

const datePickerSx = {
	width: "100%",
	minWidth: 170,
	"& .MuiInputBase-root": { height: 40, borderRadius: 1.5 },
	"& .MuiInputBase-input": { fontSize: 13 },
};

const filterItemSx = {
	flex: { xs: "1 1 100%", sm: "1 1 170px" },
	minWidth: { xs: "100%", sm: 170 },
	maxWidth: { sm: 220 },
};

const LEDGER_FIELD_MAP = {
	"RR-Electricity": "electricity",
	"RR-CAM Charges": "cam",
	"RR-Rent": "rent",
	"RR-Rent Reimbursement": "rent_reimbursement",
	"RR-Rent Revenue Share": "revenue_share",
};

const EXPENSE_KEYS = ["electricity", "cam", "rent", "rent_reimbursement", "revenue_share"];

const getRowAmount = (row) =>
	toNumber(
		row?.net_amount ??
			row?.netAmount ??
			row?.Amount ??
			row?.amount ??
			row?.Debit ??
			row?.debit ??
			row?.Value ??
			row?.value,
	);

const getRowTotal = (row) => EXPENSE_KEYS.reduce((sum, key) => sum + toNumber(row?.[key]), 0);

const withRowTotal = (row) => ({
	...row,
	total: getRowTotal(row),
});

const getRowMonthRaw = (row) =>
	row?.month ?? row?.month_year ?? row?.monthYear ?? row?.Entry_Date ?? row?.entry_date ?? row?.entryDate;

const mapNormalizedRow = (row, index, rawMonth) => {
	const monthKey = toMonthKey(rawMonth);
	return withRowTotal({
		id: row?.id ?? `${row?.admsite_code ?? row?.site_code ?? "store"}-${monthKey || "no-expense"}-${index}`,
		monthKey,
		month: monthKey ? formatMonth(rawMonth) : "-",
		monthSort: monthKey ? dayjs(rawMonth).valueOf() : 0,
		cost_center: row?.cost_center ?? row?.costCenter ?? row?.Cost_Centre ?? row?.site_name ?? "-",
		store_name: row?.store_name ?? row?.storeName ?? row?.SHRTNAME ?? "-",
		store_code: row?.store_code ?? row?.storeCode ?? "-",
		site_code: row?.admsite_code ?? row?.site_code ?? row?.siteCode ?? row?.ADMSITE_CODE ?? "-",
		store_type: row?.store_type ?? row?.storeType ?? "-",
		electricity: toNumber(row?.electricity),
		cam: toNumber(row?.cam ?? row?.CAM),
		rent: toNumber(row?.rent),
		rent_reimbursement: toNumber(row?.rent_reimbursement ?? row?.rentReimbursement),
		revenue_share: toNumber(
			row?.revenue_share ?? row?.revenueShare ?? row?.revenue_share_amount ?? row?.revenueShareAmount,
		),
	});
};

const aggregateExpenseRows = (data = []) => {
	const map = new Map();

	data.forEach((row, index) => {
		const siteCode = String(row?.admsite_code ?? row?.site_code ?? row?.siteCode ?? row?.ADMSITE_CODE ?? "").trim();
		if (!siteCode) return;

		const rawMonth = getRowMonthRaw(row);
		const monthKey = toMonthKey(rawMonth);
		// Include stores with no Entry_Date (no ledger match) as zero-expense rows
		const groupKey = monthKey ? `${siteCode}|${monthKey}` : `${siteCode}|__no_expense__`;

		if (!map.has(groupKey)) {
			map.set(groupKey, {
				...mapNormalizedRow({ ...row, admsite_code: siteCode }, index, rawMonth),
				electricity: 0,
				cam: 0,
				rent: 0,
				rent_reimbursement: 0,
				revenue_share: 0,
			});
		}

		if (!monthKey) return;

		const entry = map.get(groupKey);
		const ledger = String(row?.Ledger ?? row?.ledger ?? "").trim();
		const field = LEDGER_FIELD_MAP[ledger];
		if (field) {
			entry[field] += getRowAmount(row);
		}
	});

	return Array.from(map.values())
		.map(withRowTotal)
		.sort((a, b) => {
			const siteCompare = String(a.site_code).localeCompare(String(b.site_code));
			if (siteCompare !== 0) return siteCompare;
			return (b.monthSort || 0) - (a.monthSort || 0);
		});
};

const normalizeRows = (data = []) => {
	if (!data?.length) return [];

	const hasLedgerRows = data.some((row) => row?.Ledger ?? row?.ledger);
	if (hasLedgerRows) {
		return aggregateExpenseRows(data);
	}

	return data.map((row, index) => mapNormalizedRow(row, index, getRowMonthRaw(row)));
};

const getStoreKey = (row) => String(row.site_code || "");

const PINNED_COLUMNS = [
	{ key: "cost_center", label: "Cost Center", width: 130 },
	{ key: "store_name", label: "Store Name", width: 170 },
	{ key: "store_code", label: "Store Code", width: 105 },
	// { key: "site_code", label: "ADM Site Code", width: 110 },
	{ key: "store_type", label: "Store Type", width: 95 },
];

const SCROLL_COLUMNS = [
	{ key: "month", label: "Month", width: 100, align: "center" },
	{ key: "rent", label: "Rent", width: 110, align: "right", type: "currency" },
	{ key: "rent_reimbursement", label: "Rent Reimb.", width: 120, align: "right", type: "currency" },
	{ key: "revenue_share", label: "Revenue Share", width: 120, align: "right", type: "currency" },
	{ key: "cam", label: "CAM", width: 95, align: "right", type: "currency" },
	{ key: "electricity", label: "Electricity", width: 110, align: "right", type: "currency" },
	{ key: "total", label: "Total", width: 120, align: "right", type: "total" },
];

const getSortComparableValue = (row, key) => {
	if (key === "month") return row?.monthSort || 0;
	if (key === "total" || EXPENSE_KEYS.includes(key)) return toNumber(row?.[key]);
	return String(row?.[key] ?? "").toLowerCase();
};

const compareSortValues = (left, right, sortDir) => {
	let cmp = 0;
	if (typeof left === "number" && typeof right === "number") {
		cmp = left - right;
	} else {
		cmp = String(left).localeCompare(String(right), undefined, { numeric: true, sensitivity: "base" });
	}
	return sortDir === "desc" ? -cmp : cmp;
};

const compareRowsByKey = (left, right, key, sortDir) =>
	compareSortValues(getSortComparableValue(left, key), getSortComparableValue(right, key), sortDir);

const groupRowsByStore = (rows = [], { sortBy = "", sortDir = "asc" } = {}) => {
	const groups = [];
	const map = new Map();

	rows.forEach((row) => {
		const key = getStoreKey(row);
		if (!map.has(key)) {
			const group = { key, store: row, rows: [] };
			map.set(key, group);
			groups.push(group);
		}
		map.get(key).rows.push(row);
	});

	const isStoreColumn = PINNED_COLUMNS.some((col) => col.key === sortBy);
	const isScrollColumn = SCROLL_COLUMNS.some((col) => col.key === sortBy);

	groups.forEach((group) => {
		if (isScrollColumn) {
			group.rows.sort((a, b) => compareRowsByKey(a, b, sortBy, sortDir));
		} else {
			group.rows.sort((a, b) => (b.monthSort || 0) - (a.monthSort || 0));
		}
	});

	if (isStoreColumn) {
		groups.sort((a, b) => compareRowsByKey(a.store, b.store, sortBy, sortDir));
	} else if (isScrollColumn) {
		groups.sort((a, b) => {
			if (sortBy === "month") {
				const left = a.rows[0] || a.store;
				const right = b.rows[0] || b.store;
				return compareRowsByKey(left, right, sortBy, sortDir);
			}
			const leftSum = a.rows.reduce((sum, row) => sum + toNumber(row[sortBy]), 0);
			const rightSum = b.rows.reduce((sum, row) => sum + toNumber(row[sortBy]), 0);
			return compareSortValues(leftSum, rightSum, sortDir);
		});
	}

	return groups;
};

const getPinnedLeft = (index) => PINNED_COLUMNS.slice(0, index).reduce((sum, col) => sum + col.width, 0);

const pinnedCellSx = (index, isHeader = false, rowBg = "#ffffff") => ({
	position: "sticky",
	left: getPinnedLeft(index),
	zIndex: isHeader ? 4 : 2,
	minWidth: PINNED_COLUMNS[index].width,
	width: PINNED_COLUMNS[index].width,
	maxWidth: PINNED_COLUMNS[index].width,
	backgroundColor: isHeader ? "#dbeafe" : rowBg,
	borderRight: index === PINNED_COLUMNS.length - 1 ? "2px solid #93c5fd" : "1px solid #bfdbfe",
	boxShadow: index === PINNED_COLUMNS.length - 1 ? "4px 0 8px -4px rgba(15, 23, 42, 0.12)" : "none",
	fontWeight: isHeader ? 700 : 400,
	fontSize: 12,
	py: 1.25,
	px: 1,
	verticalAlign: "middle",
});

const headerCellSx = {
	backgroundColor: "#dbeafe",
	fontWeight: 700,
	fontSize: 12,
	borderBottom: "1px solid #bfdbfe",
	borderRight: "1px solid #bfdbfe",
	py: 1.25,
	px: 1,
	whiteSpace: "nowrap",
};

const bodyCellSx = (align = "left") => ({
	fontSize: 12,
	borderBottom: "1px solid #eceff1",
	borderRight: "1px solid #eceff1",
	py: 1,
	px: 1,
	textAlign: align,
	verticalAlign: "middle",
});

const ROLE_TAG_STYLES = {
	VIEWER: { backgroundColor: "#f1f5f9", color: "#475569" },
	EDITOR: { backgroundColor: "#fffbeb", color: "#b45309" },
	PUBLISHER: { backgroundColor: "#f5f3ff", color: "#7c3aed" },
	ADMIN: { backgroundColor: "#ecfdf5", color: "#047857" },
};

const renderStoreType = (value) => {
	const type = String(value || "-").toUpperCase();
	const styles = {
		COCO: { backgroundColor: "#fef3c7", color: "#92400e" },
		COFO: { backgroundColor: "#dbeafe", color: "#1e40af" },
		FOFO: { backgroundColor: "#fce7f3", color: "#9d174d" },
		EBO: { backgroundColor: "#ecfdf5", color: "#047857" },
	};
	const chipStyle = styles[type] || { backgroundColor: "#f3f4f6", color: "#374151" };

	return (
		<Chip
			size="small"
			label={type}
			sx={{ fontWeight: 600, fontSize: 11, height: 24, ...chipStyle }}
		/>
	);
};

const renderMonth = (value) => (
	<Chip
		size="small"
		label={value || "-"}
		sx={{ fontWeight: 600, fontSize: 11, backgroundColor: "#eef2ff", color: "#3730a3", height: 24 }}
	/>
);

const renderCurrency = (value) => (
	<Typography variant="body2" fontWeight={600} color="#0f766e" sx={{ fontSize: 12 }}>
		{formatCurrency(value)}
	</Typography>
);

const renderCellValue = (row, col) => {
	if (col.type === "total") {
		return (
			<Typography variant="body2" fontWeight={700} color="#1e40af" sx={{ fontSize: 12 }}>
				{formatCurrency(row.total)}
			</Typography>
		);
	}
	if (col.type === "currency") return renderCurrency(row[col.key]);
	if (col.key === "month") return renderMonth(row.month);
	if (col.key === "store_type") return renderStoreType(row.store_type);
	return row[col.key] || "-";
};

const StatCard = ({ icon, label, value, sub, accent = "#1e40af", bg = "#eff6ff" }) => (
	<Paper
		elevation={0}
		sx={{
			flex: 1,
			minWidth: 170,
			p: 2,
			borderRadius: 2,
			border: "1px solid #e5e7eb",
			background: `linear-gradient(135deg, ${bg} 0%, #ffffff 100%)`,
		}}>
		<Stack direction="row" spacing={1.5} alignItems="flex-start">
			<Box
				sx={{
					width: 40,
					height: 40,
					borderRadius: 1.5,
					display: "flex",
					alignItems: "center",
					justifyContent: "center",
					color: accent,
					backgroundColor: "#ffffff",
					border: `1px solid ${accent}22`,
				}}>
				{icon}
			</Box>
			<Box>
				<Typography variant="caption" color="text.secondary" fontWeight={600} letterSpacing={0.4}>
					{label}
				</Typography>
				<Typography variant="h6" fontWeight={700} sx={{ color: accent, lineHeight: 1.2 }}>
					{value}
				</Typography>
				{sub && (
					<Typography variant="caption" color="text.secondary">
						{sub}
					</Typography>
				)}
			</Box>
		</Stack>
	</Paper>
);

export default function ExpensesDashboard() {
	const [rows, setRows] = useState([]);
	const [rawRows, setRawRows] = useState([]);
	const [search, setSearch] = useState("");
	const [sortBy, setSortBy] = useState("");
	const [sortDir, setSortDir] = useState("asc");
	const [fromDate, setFromDate] = useState(dayjs().startOf("month"));
	const [toDate, setToDate] = useState(dayjs());
	const [appliedFromDate, setAppliedFromDate] = useState(null);
	const [appliedToDate, setAppliedToDate] = useState(null);
	const [rangeError, setRangeError] = useState("");
	const [page, setPage] = useState(0);
	const [rowsPerPage, setRowsPerPage] = useState(25);
	const { setLoadingOpen, setSnackBar, setSnackBarOpen } = GlobalState();
	const { user } = AuthState();
	const roleData = eboStoreExpensesRoleAccess(user);
	const canDownload = canDownloadEboStoreExpenses(roleData.roles);

	const handleFetchData = async () => {
		if (!fromDate?.isValid() || !toDate?.isValid()) {
			setRangeError("Please select both From and To dates");
			setRows([]);
			setRawRows([]);
			setAppliedFromDate(null);
			setAppliedToDate(null);
			return;
		}
		if (fromDate.isAfter(toDate, "day")) {
			setRangeError("From date must be earlier than or equal to To date");
			setRows([]);
			setRawRows([]);
			setAppliedFromDate(null);
			setAppliedToDate(null);
			return;
		}

		setRangeError("");
		try {
			setLoadingOpen(true);
			const res = await pythonVmInstance({
				url: "/ebo-store-expenses/dashboard",
				method: "GET",
				headers: { "Content-Type": "application/json" },
				params: {
					start_date: fromDate.format("YYYY-MM-DD"),
					end_date: toDate.format("YYYY-MM-DD"),
				},
			});
			const data = Array.isArray(res?.data) ? res.data : res?.data?.rows || res?.data?.data || [];
			setRawRows(data);
			setRows(filterByDateRange(normalizeRows(data), fromDate, toDate));
			setAppliedFromDate(fromDate);
			setAppliedToDate(toDate);
		} catch {
			setRawRows([]);
			setRows([]);
			setAppliedFromDate(null);
			setAppliedToDate(null);
			setSnackBarOpen(true);
			setSnackBar({
				message: "Failed to fetch expenses. Please try again.",
				severity: "warning",
				anchorOrigin: { horizontal: "center", vertical: "top" },
			});
		} finally {
			setLoadingOpen(false);
		}
	};

	useEffect(() => {
		setPage(0);
	}, [search, sortBy, sortDir, rows]);

	const filteredRows = useMemo(() => {
		const query = search.trim().toLowerCase();
		if (!query) return rows;
		return rows.filter((row) =>
			[row.month, row.cost_center, row.store_name, row.store_code, row.site_code, row.store_type].some((value) =>
				String(value || "").toLowerCase().includes(query),
			),
		);
	}, [rows, search]);

	const groupedStores = useMemo(
		() => groupRowsByStore(filteredRows, { sortBy, sortDir }),
		[filteredRows, sortBy, sortDir],
	);

	const recordCount = useMemo(
		() => groupedStores.reduce((count, group) => count + group.rows.length, 0),
		[groupedStores],
	);

	const paginatedStores = useMemo(() => {
		const start = page * rowsPerPage;
		return groupedStores.slice(start, start + rowsPerPage);
	}, [groupedStores, page, rowsPerPage]);

	const summary = useMemo(() => {
		return filteredRows.reduce(
			(acc, row) => {
				acc.electricity += toNumber(row.electricity);
				acc.cam += toNumber(row.cam);
				acc.rent += toNumber(row.rent);
				acc.rent_reimbursement += toNumber(row.rent_reimbursement);
				acc.revenue_share += toNumber(row.revenue_share);
				acc.total += toNumber(row.total);
				return acc;
			},
			{ electricity: 0, cam: 0, rent: 0, rent_reimbursement: 0, revenue_share: 0, total: 0 },
		);
	}, [filteredRows]);

	const handleDownloadRaw = () => {
		if (!canDownload) {
			setSnackBarOpen(true);
			setSnackBar({
				message: "You do not have permission to download this data.",
				severity: "warning",
				anchorOrigin: { horizontal: "center", vertical: "top" },
			});
			return;
		}

		if (!rawRows.length) {
			setSnackBarOpen(true);
			setSnackBar({
				message: "No raw data available to download.",
				severity: "warning",
				anchorOrigin: { horizontal: "center", vertical: "top" },
			});
			return;
		}

		const from = fromDate?.isValid() ? fromDate.format("YYYY-MM-DD") : "from";
		const to = toDate?.isValid() ? toDate.format("YYYY-MM-DD") : "to";
		downloadRawCsv(rawRows, `ebo_store_expenses_raw_${from}_${to}.csv`);
	};

	const handleSort = (columnKey) => {
		if (sortBy === columnKey) {
			if (sortDir === "asc") {
				setSortDir("desc");
			} else {
				setSortBy("");
				setSortDir("asc");
			}
			return;
		}
		setSortBy(columnKey);
		setSortDir("asc");
	};

	const renderSortableHeader = (col, extraSx = {}) => (
		<TableCell
			key={col.key}
			align={col.align || "left"}
			sortDirection={sortBy === col.key ? sortDir : false}
			sx={{
				...extraSx,
				minWidth: col.width,
				width: col.width,
				cursor: "pointer",
				userSelect: "none",
			}}
			onClick={() => handleSort(col.key)}>
			<TableSortLabel
				active={sortBy === col.key}
				direction={sortBy === col.key ? sortDir : "asc"}
				sx={{
					width: "100%",
					justifyContent: col.align === "right" ? "flex-end" : col.align === "center" ? "center" : "flex-start",
					"& .MuiTableSortLabel-icon": { fontSize: 16 },
					color: "inherit !important",
					fontWeight: 700,
					fontSize: 12,
				}}>
				{col.label}
			</TableSortLabel>
		</TableCell>
	);

	return (
		<Box px={2} pb={3}>
			<Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" alignItems={{ md: "center" }} gap={1} mb={2}>
				<Box>
					<Typography variant="h6" fontWeight={700} color="#1e293b">
						Store Expenses Overview
					</Typography>
					<Typography variant="body2" color="text.secondary">
						Monthly expense breakdown by store — electricity, CAM, rent & revenue share
					</Typography>
				</Box>
				<Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
					<Chip
						label={`${groupedStores.length} site${groupedStores.length === 1 ? "" : "s"} · ${recordCount} month records`}
						size="small"
						sx={{ fontWeight: 600, backgroundColor: "#dbeafe", color: "#1e40af" }}
					/>
					<Chip
						label={`Access: ${roleData.roles}`}
						size="small"
						sx={{
							fontWeight: 600,
							...(ROLE_TAG_STYLES[roleData.roles] || ROLE_TAG_STYLES.VIEWER),
						}}
					/>
				</Stack>
			</Stack>

			<Stack direction={{ xs: "column", lg: "row" }} spacing={1.5} mb={2} flexWrap="wrap" useFlexGap>
				<StatCard
					icon={<StorefrontOutlinedIcon fontSize="small" />}
					label="Total Expenses"
					value={formatCurrency(summary.total)}
					sub="Electricity + CAM + Rent + Reimbursement + Revenue Share"
					accent="#1e40af"
				/>
				<StatCard
					icon={<BoltOutlinedIcon fontSize="small" />}
					label="Electricity"
					value={formatCurrency(summary.electricity)}
					accent="#0f766e"
					bg="#ecfdf5"
				/>
				<StatCard
					icon={<HomeWorkOutlinedIcon fontSize="small" />}
					label="Rent"
					value={formatCurrency(summary.rent)}
					accent="#7c3aed"
					bg="#f5f3ff"
				/>
				<StatCard
					icon={<AccountBalanceWalletOutlinedIcon fontSize="small" />}
					label="Rent Reimbursement"
					value={formatCurrency(summary.rent_reimbursement)}
					accent="#b45309"
					bg="#fffbeb"
				/>
				<StatCard
					icon={<MonetizationOnOutlinedIcon fontSize="small" />}
					label="Revenue Share"
					value={formatCurrency(summary.revenue_share)}
					accent="#be123c"
					bg="#fff1f2"
				/>
			</Stack>

			<Paper elevation={0} sx={{ p: 2, mb: 2, borderRadius: 2, border: "1px solid #e5e7eb" }}>
				<Stack spacing={1.5}>
					<TextField
						size="small"
						placeholder="Search by cost center, store name or code..."
						value={search}
						onChange={(e) => setSearch(e.target.value)}
						fullWidth
						InputProps={{
							startAdornment: (
								<InputAdornment position="start">
									<SearchIcon fontSize="small" color="action" />
								</InputAdornment>
							),
						}}
					/>
					<LocalizationProvider dateAdapter={AdapterDayjs}>
						<Box
							sx={{
								display: "flex",
								flexWrap: "wrap",
								gap: 1.5,
								alignItems: "center",
							}}>
							<Box sx={filterItemSx}>
								<DatePicker
									label="From"
									format="DD MMM YYYY"
									value={fromDate}
									maxDate={dayjs()}
									onChange={(value) => {
										const nextFrom = value || null;
										setFromDate(nextFrom);
										setToDate((prevTo) =>
											prevTo?.isValid() && nextFrom?.isValid() && prevTo.isBefore(nextFrom, "day")
												? null
												: prevTo,
										);
										setRangeError("");
									}}
									slotProps={{ textField: { size: "small", fullWidth: true, sx: datePickerSx } }}
								/>
							</Box>
							<Box sx={filterItemSx}>
								<DatePicker
									label="To"
									format="DD MMM YYYY"
									value={toDate}
									minDate={fromDate?.isValid() ? fromDate : undefined}
									maxDate={dayjs()}
									disabled={!fromDate?.isValid()}
									onChange={(value) => {
										setToDate(value || null);
										setRangeError("");
									}}
									slotProps={{ textField: { size: "small", fullWidth: true, sx: datePickerSx } }}
								/>
							</Box>
							<Button
								variant="contained"
								size="small"
								startIcon={<TravelExploreOutlinedIcon />}
								onClick={handleFetchData}
								disabled={!fromDate?.isValid() || !toDate?.isValid()}
								sx={{
									height: 40,
									px: 2,
									flex: { xs: "1 1 100%", sm: "0 0 auto" },
									minWidth: { xs: "100%", sm: 130 },
									whiteSpace: "nowrap",
									boxShadow: "none",
									"&:hover": { boxShadow: "none" },
								}}>
								Fetch Data
							</Button>
							<Button
								variant="outlined"
								size="small"
								startIcon={<FileDownloadOutlinedIcon />}
								onClick={handleDownloadRaw}
								disabled={!canDownload || !rawRows.length || !!rangeError}
								sx={{
									height: 40,
									px: 2,
									flex: { xs: "1 1 100%", sm: "0 0 auto" },
									minWidth: { xs: "100%", sm: 160 },
									whiteSpace: "nowrap",
								}}>
								Download Raw Data
							</Button>
						</Box>
					</LocalizationProvider>
				</Stack>
				{rangeError && (
					<Typography variant="caption" color="error" sx={{ mt: 1, display: "block" }}>
						{rangeError}
					</Typography>
				)}
				{appliedFromDate?.isValid() && appliedToDate?.isValid() && !rangeError ? (
					<Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: "block" }}>
						Showing expenses from {appliedFromDate.format("DD MMM YYYY")} to {appliedToDate.format("DD MMM YYYY")}
					</Typography>
				) : (
					<Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: "block" }}>
						Select a date range and click Fetch Data to load expenses
					</Typography>
				)}
			</Paper>

			<Paper elevation={0} sx={{ borderRadius: 2, overflow: "hidden", border: "1px solid #e5e7eb", boxShadow: "0px 1px 3px rgba(0,0,0,0.08)", width: "100%" }}>
				<TableContainer sx={{ maxHeight: 520 }}>
					<Table stickyHeader size="small" sx={{ minWidth: 1265, tableLayout: "fixed" }}>
						<TableHead>
							<TableRow>
								{PINNED_COLUMNS.map((col, index) =>
									renderSortableHeader(col, { ...headerCellSx, ...pinnedCellSx(index, true) }),
								)}
								{SCROLL_COLUMNS.map((col) => renderSortableHeader(col, headerCellSx))}
							</TableRow>
						</TableHead>
						<TableBody>
							{paginatedStores.map((group, groupIndex) => {
								const rowBg = groupIndex % 2 === 0 ? "#ffffff" : "#f8fafc";
								return group.rows.map((row, rowIndex) => (
									<TableRow
										key={row.id}
										hover
										sx={{
											backgroundColor: rowBg,
											"&:hover": { backgroundColor: "#eff6ff" },
											"&:hover .pinned-store-cell": { backgroundColor: "#eff6ff" },
										}}>
										{rowIndex === 0 &&
											PINNED_COLUMNS.map((col, index) => (
												<TableCell
													key={`${group.key}-${col.key}`}
													rowSpan={group.rows.length}
													align="left"
													className="pinned-store-cell"
													sx={{
														...pinnedCellSx(index, false, rowBg),
														fontWeight: col.key === "store_name" ? 600 : 400,
													}}>
													{col.key === "store_type" ? (
														renderStoreType(group.store[col.key])
													) : (
														<Typography variant="body2" noWrap title={group.store[col.key]} sx={{ fontSize: 12 }}>
															{group.store[col.key] || "-"}
														</Typography>
													)}
												</TableCell>
											))}
										{SCROLL_COLUMNS.map((col) => (
											<TableCell key={`${row.id}-${col.key}`} align={col.align} sx={bodyCellSx(col.align)}>
												{col.align === "right" ? (
													<Box display="flex" justifyContent="flex-end">{renderCellValue(row, col)}</Box>
												) : col.align === "center" ? (
													<Box display="flex" justifyContent="center">{renderCellValue(row, col)}</Box>
												) : (
													renderCellValue(row, col)
												)}
											</TableCell>
										))}
									</TableRow>
								));
							})}
							{!groupedStores.length && (
								<TableRow>
									<TableCell colSpan={PINNED_COLUMNS.length + SCROLL_COLUMNS.length} align="center" sx={{ py: 4 }}>
										<Typography color="text.secondary">No expense records found</Typography>
									</TableCell>
								</TableRow>
							)}
						</TableBody>
					</Table>
				</TableContainer>
				<TablePagination
					component="div"
					count={groupedStores.length}
					page={page}
					onPageChange={(_, newPage) => setPage(newPage)}
					rowsPerPage={rowsPerPage}
					onRowsPerPageChange={(event) => {
						setRowsPerPage(parseInt(event.target.value, 10));
						setPage(0);
					}}
					rowsPerPageOptions={[10, 25, 50, 100]}
					labelRowsPerPage="Sites per page"
					sx={{
						borderTop: "1px solid #dbeafe",
						backgroundColor: "#f8fafc",
						"& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows": {
							fontSize: 13,
						},
					}}
				/>
			</Paper>
		</Box>
	);
}
